// API Security — Schema Validation page (redesigned).
//
// Layout matches the Anthropic design bundle handoff
// (qm23Q5lrFwZhQrH4aeGw4Q):
//   - Four hero metric cards (mode + active spec, 24h volume, would-block,
//     conformant %)
//   - Specs table with active-row left-border accent + activate toggle
//   - Operations explorer in the active spec (search + method select
//     with counts + per-row sparkline + invalid badge)
//   - Right rail: Validation settings (segmented mode + 3 toggles),
//     Upload card (drop-zone + name/version/format), Recent invalid feed
//
// All numbers come from real backends:
//   - /spec/specs, /spec/specs/active, /spec/policy — existing PG
//   - /spec/stats/24h, /spec/ops_activity, /spec/recent_invalid —
//     CH aggregates over layerd-v2-spec-validation-events
//
// No mocked data anywhere. Hero metrics fall to "-" when CH returns
// zero rows so operators can tell "quiet" from "broken".

import React from "react";
import {
  Stack, Typography, Box, Grid, IconButton, MenuItem, Button,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Select, CircularProgress, Skeleton, TablePagination} from "@mui/material";
import UploadIcon from "@mui/icons-material/UploadFile";
import UploadFileIcon from "@mui/icons-material/UploadFileOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooksOutlined";
import RouteIcon from "@mui/icons-material/AltRoute";
import SearchIcon from "@mui/icons-material/Search";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import {
  Upload, PowerOff, Eye, ShieldCheck,
  Shield, Activity, AlertTriangle, CheckCircle2,
} from "lucide-react";

import asec from "../../../../../utils/axios/v1/asecAxios";
import SectionHelp from "./components/SectionHelp";
import colors from "../../../../../utils/colors";
import { formatDate } from "../../../../../utils/format";
import LayerdIcon from "../../../../common/LayerdIcon";
import RefreshIconButton from "../../../../common/RefreshIconButton";
import { PrimaryButton, GhostButton } from "../../../../common/Button";
import { useNotification } from "../../../../../contexts/NotificationContext";
import { GreenSwitch } from "../../../../common/Greens";
import AsecPage from "./AsecPage";
import {
  AsecDeltaSub, AsecMetricStrip, AsecModeSegmented, AsecStatusPill, AsecHeroModeValue, AsecHeroStatValue,
} from "./components/asecPrimitives";
import useAsecPermissions from "./useAsecPermissions";
import StickySaveBar from "../../../../common/StickySaveBar";
import { collectObjectDiff, enabledLabel, fieldChange } from "../../../../common/stickySaveBarUtils";
import { asecFormPrimaryButtonSx, AsecTextField, filterSelectSx } from "./asecUi";
import { SafeTextField } from "../../../../common/Greens";

const INDIGO = colors.primary[300];
const INDIGO_INK = colors.primary[500];
const INDIGO_DEEP = colors.primary[400];
const INDIGO_SOFT = "rgba(99,113,247,0.10)";
const INDIGO_SOFTER = "rgba(99,113,247,0.05)";
const INDIGO_MID = "rgba(99,113,247,0.18)";
const INDIGO_BORDER = "rgba(99,113,247,0.30)";
const GREEN_DARK = colors.green[300];
const RED = colors.red[100];
const RED_DARK = colors.red[200];
const YELLOW = colors.yellow[200];
const FAINT_BLUE = "#F5F6FE"; // very faint indigo wash for backgrounds
const FAINT_GREY = "#FAFAFB";

// ─── Format / number helpers ────────────────────────────────────────
const nfmt = (n) => (typeof n === "number" && isFinite(n) ? n.toLocaleString() : "—");
const pct = (n, d = 2) => (typeof n === "number" && isFinite(n) ? `${n.toFixed(d)}%` : "—");
const relTime = (ts) => {
  if (!ts) return "—";
  const ms = Date.now() - new Date(ts).getTime();
  if (ms < 60_000) return `${Math.max(1, Math.floor(ms / 1000))}s ago`;
  if (ms < 3600_000) return `${Math.floor(ms / 60_000)} min ago`;
  if (ms < 86400_000) return `${Math.floor(ms / 3600_000)} h ago`;
  return `${Math.floor(ms / 86400_000)} d ago`;
};

// ─── Method colour palette (matches design) ─────────────────────────
const methodPalette = (m) => {
  const k = (m || "").toUpperCase();
  if (k === "GET")    return { bg: "rgba(31,193,107,0.10)",  fg: GREEN_DARK };
  if (k === "POST")   return { bg: "rgba(99,113,247,0.10)",  fg: INDIGO };
  if (k === "PUT")    return { bg: "rgba(223,180,0,0.12)",   fg: "#7a5b10" };
  if (k === "PATCH")  return { bg: "rgba(99,113,247,0.10)",  fg: INDIGO_INK };
  if (k === "DELETE") return { bg: "rgba(251,55,72,0.10)",   fg: RED_DARK };
  return { bg: colors.neutral[100], fg: colors.neutral[700] };
};

function MethodTag({ method, sx }) {
  const p = methodPalette(method);
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      minWidth: 60, height: 22, px: 1, borderRadius: "5px",
fontSize: 12, fontWeight: 700, letterSpacing: "0.04em",
      backgroundColor: p.bg, color: p.fg, ...sx,
    }}>
      {(method || "").toUpperCase()}
    </Box>
  );
}

function Pill({ tone = "default", children, sx }) {
  const tones = {
    default: { bg: colors.neutral[100], fg: colors.neutral[700], bd: colors.neutral[200] },
    ok:      { bg: "#E8FBEF", fg: "#0f7a42",           bd: "#C0EED4" },
    warn:    { bg: "rgba(255,219,67,0.18)", fg: "#7a5b10",            bd: "rgba(223,180,0,0.40)" },
    danger:  { bg: "rgba(251,55,72,0.10)",  fg: RED_DARK,             bd: "rgba(251,55,72,0.30)" },
    info:    { bg: "rgba(99,113,247,0.10)", fg: INDIGO_INK,           bd: "rgba(99,113,247,0.24)" },
  };
  const t = tones[tone] || tones.default;
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: 0.5,
      px: 1.1, height: 22, borderRadius: 999,
      fontSize: 12, fontWeight: 500, whiteSpace: "nowrap",
      backgroundColor: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
      ...sx,
    }}>
      {children}
    </Box>
  );
}

// ─── Tiny inline sparkline (no chart lib needed) ────────────────────
function Sparkline({ data = [], color = INDIGO, width = 56, height = 16 }) {
  if (!data.length) return null;
  const max = Math.max(1, ...data);
  const stepX = width / Math.max(1, data.length - 1);
  const points = data.map((v, i) => `${i * stepX},${height - (v / max) * (height - 2) - 1}`).join(" ");
  return (
    <svg width={width} height={height} style={{ verticalAlign: "middle" }}>
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
    </svg>
  );
}

// ─── Common cell + helpers ──────────────────────────────────────────
const cellSx = {
fontSize: 14, color: colors.neutral[1000],
  borderBottom: `1px solid ${colors.neutral[200]}`,
  p: "13px 16px", verticalAlign: "middle",
};
const headCellSx = {
fontSize: 12, fontWeight: 700, letterSpacing: "0.05em",
  textTransform: "uppercase", color: colors.neutral[700],
  borderBottom: `1px solid ${colors.neutral[200]}`,
  backgroundColor: FAINT_GREY, p: "11px 16px",
};
const dotSx = { width: 6, height: 6, borderRadius: "50%", backgroundColor: "currentColor", display: "inline-block" };

function prettyFormat(f) {
  if (f === "openapi3") return "OpenAPI 3";
  if (f === "graphql") return "GraphQL SDL";
  if (f === "protobuf") return "Protobuf";
  return f || "—";
}

function statusCodeStyle(code) {
  const s = String(code);
  if (s.startsWith("2")) return { backgroundColor: "rgba(31,193,107,0.10)", color: GREEN_DARK };
  if (s.startsWith("4")) return { backgroundColor: "rgba(255,219,67,0.18)", color: "#7a5b10" };
  if (s.startsWith("5")) return { backgroundColor: "rgba(251,55,72,0.10)", color: RED_DARK };
  return { backgroundColor: colors.neutral[100], color: colors.neutral[700] };
}

function heroStatSkeleton(width = 120) {
  return <Skeleton variant="text" width={width} height={32} sx={{ borderRadius: "6px" }} />;
}

function buildHeroMetrics(stats, statsLoading, policy) {
  const mode = policy?.mode || "off";
  const modeLabel = mode === "log_only" ? "Log only" : mode === "enforce" ? "Enforce" : "Off";
  const modeTone = mode === "enforce"
    ? { kind: "danger", lbl: "Enforcing" }
    : mode === "log_only"
      ? { kind: "warn", lbl: "Observing" }
      : { kind: "default", lbl: "Disabled" };

  return [
    {
      key: "mode",
      icon: Shield,
      highlight: true,
      label: "Validation mode",
      value: (
        <AsecHeroModeValue label={modeLabel} pillKind={modeTone.kind} pillLabel={modeTone.lbl} />
      ),
    },
    {
      key: "checked",
      icon: Activity,
      label: "Requests checked · 24h",
      value: statsLoading ? heroStatSkeleton(120) : nfmt(stats?.total_24h),
      sub: typeof stats?.delta_pct === "number" ? <AsecDeltaSub deltaPct={stats.delta_pct} /> : null,
    },
    {
      key: "block",
      icon: AlertTriangle,
      label: "Would block",
      value: statsLoading ? heroStatSkeleton(80) : nfmt(stats?.would_block_24h),
      valueSx: { color: "#7a5b10" },
      sub: `${pct(stats?.would_block_pct)} of traffic`,
    },
    {
      key: "conformant",
      icon: CheckCircle2,
      label: "Conformant",
      value: statsLoading ? heroStatSkeleton(100) : pct(stats?.conformant_pct),
      valueSx: { color: INDIGO_DEEP },
      sub: `${nfmt(stats?.valid_24h)} valid requests`,
    },
  ];
}

// ─── SPECS TABLE ────────────────────────────────────────────────────
function SpecsCard({ specs, activeSpecId, onActiveChange, onSelect, canConfigure }) {
  const total = specs?.length ?? 0;
  const activeCount = specs?.filter((s) => s.id === activeSpecId).length ?? 0;
  const drafts = total - activeCount;
  return (
    <Box sx={{
      backgroundColor: colors.neutral[100], borderRadius: "12px",
      border: `1px solid ${colors.neutral[200]}`,
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ p: "16px 20px", borderBottom: `1px solid ${colors.neutral[200]}`, backgroundColor: FAINT_BLUE }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <LibraryBooksIcon sx={{ fontSize: 16, color: INDIGO }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Specs</Typography> <SectionHelp tooltip="Every OpenAPI spec uploaded to this site. The row with the indigo left-border is the active spec — that's what Layerd validates against. Click any row to inspect its operations below." size="small" />
          <Pill sx={{ ml: 0.5 }}>{activeCount} active · {drafts} drafts</Pill>
        </Stack>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {["Name", "Format", "Version", "Operations", "Imported", "Status", "Active"].map((h, i) => (
                <TableCell key={h} align={i === 6 ? "right" : "left"} sx={{
                  ...headCellSx, width: i === 0 ? "34%" : undefined,
                }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(specs || []).map((s) => {
              const isActive = activeSpecId === s.id;
              return (
                <TableRow key={s.id} hover onClick={() => onSelect(s)}
                  sx={{
                    cursor: "pointer", opacity: isActive ? 1 : 0.65,
                    backgroundColor: isActive ? INDIGO_SOFTER : "transparent",
                    "& td:first-of-type": isActive ? { borderLeft: `3px solid ${INDIGO}`, pl: "13px" } : {},
                    transition: "background 120ms",
                  }}>
                  <TableCell sx={cellSx}>
                    <Stack spacing={0.25}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{
                          width: 14, height: 14, borderRadius: "3px",
                          backgroundColor: isActive ? INDIGO_SOFT : colors.neutral[100],
                          color: isActive ? INDIGO : colors.neutral[600],
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
fontSize: 12, fontWeight: 700,
                        }}>{`{}`}</Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{s.name}</Typography>
                      </Stack>
                      <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>
                        {isActive
                          ? "Authoritative · used for validation"
                          : `Uploaded ${s.imported_at ? new Date(s.imported_at).toLocaleDateString() : "—"} · not in use`}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Pill tone={s.format === "openapi3" ? "info" : "default"}>{prettyFormat(s.format)}</Pill>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, fontSize: 12 }}>{s.version || "—"}</TableCell>
                  <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums" }}>{s._op_count ?? "—"}</TableCell>
                  <TableCell sx={cellSx}>
                    <Typography sx={{ fontSize: 12 }}>
                      {s.imported_at ? formatDate(s.imported_at) : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {isActive
                      ? <Pill tone="ok"><Box sx={dotSx} />Enabled</Pill>
                      : <Pill><Box sx={{ ...dotSx, backgroundColor: colors.neutral[500] }} />Disabled</Pill>}
                  </TableCell>
                  <TableCell sx={cellSx} align="right" onClick={(e) => e.stopPropagation()}>
                    <GreenSwitch
                      checked={isActive}
                      disabled={!canConfigure}
                      onChange={(e) => {
                        e.stopPropagation();
                        if (!canConfigure || !e.target.checked) return;
                        onActiveChange(s.id);
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {specs && specs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: colors.neutral[600] }}>
                  No specs uploaded yet. Use the Upload card on the right to add one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── OPERATIONS EXPLORER ────────────────────────────────────────────
const OPS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const OPS_DEFAULT_PAGE_SIZE = 10;
const OPS_PARAMS_COL_WIDTH = 160;
const OPS_PATH_COL_WIDTH = 140;
const opsParamsColSx = {
  width: OPS_PARAMS_COL_WIDTH,
  maxWidth: OPS_PARAMS_COL_WIDTH,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const opsPathColSx = {
  width: OPS_PATH_COL_WIDTH,
  maxWidth: OPS_PATH_COL_WIDTH,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: 12,
};

function OperationsExplorer({
  selectedSpec, rows, activityByKey,
  search, setSearch, methodFilter, setMethodFilter,
  loading, live, setLive, onRefresh,
}) {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(OPS_DEFAULT_PAGE_SIZE);

  const filtered = React.useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    return (rows || []).filter((r) => {
      if (methodFilter !== "ALL" && r.method !== methodFilter) return false;
      if (!q) return true;
      return r.path.toLowerCase().includes(q) || (r.summary || "").toLowerCase().includes(q);
    });
  }, [rows, search, methodFilter]);

  React.useEffect(() => {
    setPage(0);
  }, [search, methodFilter, selectedSpec?.id, rows?.length]);

  const paginated = React.useMemo(
    () => filtered.slice(page * pageSize, page * pageSize + pageSize),
    [filtered, page, pageSize]
  );

  const maxPage = Math.max(0, Math.ceil(filtered.length / pageSize) - 1);
  React.useEffect(() => {
    if (page > maxPage) setPage(maxPage);
  }, [page, maxPage]);

  const methodCounts = React.useMemo(() => {
    const c = { ALL: rows?.length || 0 };
    (rows || []).forEach((r) => { c[r.method] = (c[r.method] || 0) + 1; });
    return c;
  }, [rows]);
  const methods = ["GET", "POST", "PUT", "PATCH", "DELETE"].filter((m) => methodCounts[m]);

  return (
    <Box sx={{
      backgroundColor: colors.neutral[100], borderRadius: "12px",
      border: `1px solid ${colors.neutral[200]}`, overflow: "hidden",
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ p: "16px 20px", borderBottom: `1px solid ${colors.neutral[200]}`, backgroundColor: FAINT_BLUE }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <RouteIcon sx={{ fontSize: 17, color: INDIGO }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            Operations in{" "}
            <Box component="span" sx={ { color: INDIGO }}>
              {selectedSpec?.name || "—"}
            </Box>
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={0.75}
            sx={{ fontSize: 12, color: colors.neutral[700], cursor: "pointer" }}
            onClick={() => setLive(!live)}>
            <Box sx={{
              width: 7, height: 7, borderRadius: "50%",
              backgroundColor: live ? INDIGO : colors.neutral[400],
              boxShadow: live ? `0 0 0 4px ${INDIGO_MID}` : "none",
            }} />
            {live ? "Live · 15s refresh" : "Paused"}
          </Stack>
          <RefreshIconButton
            onClick={onRefresh}
            disabled={loading}
            loading={loading}
            size="compact"
          />
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}
        sx={{ p: "14px 20px", borderBottom: `1px solid ${colors.neutral[200]}`,
          backgroundColor: FAINT_GREY, flexWrap: "wrap", rowGap: 1 }}>
        <Box sx={{
          flex: 1, minWidth: 200, display: "flex", alignItems: "center", gap: 1,
          height: 34, px: 1.5, borderRadius: "8px",
          backgroundColor: colors.neutral[100], border: `1px solid ${colors.neutral[200]}`,
        }}>
          <SearchIcon sx={{ fontSize: 14, color: colors.neutral[500] }} />
          <SafeTextField
            variant="standard"
            placeholder="Search path or operation…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ disableUnderline: true, sx:  { fontSize: 14 } }}
            sx={{ flex: 1 }}
          />
        </Box>
        <Select
          value={methodFilter}
          onChange={(e) => setMethodFilter(e.target.value)}
          sx={{ ...filterSelectSx, minWidth: 140 }}
        >
          <MenuItem value="ALL">All methods ({nfmt(methodCounts.ALL || 0)})</MenuItem>
          {methods.map((m) => (
            <MenuItem key={m} value={m}>
              {m} ({nfmt(methodCounts[m])})
            </MenuItem>
          ))}
        </Select>
      </Stack>

      <TableContainer>
        <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow>
              {[
                ["Method", 90, "left"],
                ["Path", OPS_PATH_COL_WIDTH, "left"],
                ["Summary", undefined, "left"],
                ["Parameters", OPS_PARAMS_COL_WIDTH, "left"],
                ["Responses", 120, "left"],
                ["24h activity", 170, "right"],
              ].map(([label, w, align], i) => (
                <TableCell
                  key={i}
                  align={align}
                  sx={{
                    ...headCellSx,
                    width: w,
                    ...(label === "Path" ? opsPathColSx : {}),
                    ...(label === "Parameters" ? opsParamsColSx : {}),
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!selectedSpec ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, color: colors.neutral[600] }}>
                  Select a spec on the left to see its operations.
                </TableCell>
              </TableRow>
            ) : loading && (!rows || rows.length === 0) ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}><CircularProgress size={16} /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, color: colors.neutral[600] }}>
                  {rows?.length ? "No operations match the current filter." : "No operations parsed from this spec."}
                </TableCell>
              </TableRow>
            ) : paginated.map((r) => {
              const act = activityByKey[`${r.method} ${r.path}`] || null;
              const invalid = act?.invalid_count || 0;
              const rowKey = `${r.method}:${r.path}`;
              return (
                <TableRow key={rowKey} hover sx={{ backgroundColor: invalid > 0 ? "rgba(255,219,67,0.05)" : "transparent" }}>
                  <TableCell sx={cellSx}><MethodTag method={r.method} /></TableCell>
                  <TableCell sx={{ ...cellSx, ...opsPathColSx }} title={r.path}>{r.path}</TableCell>
                  <TableCell sx={{ ...cellSx, color: colors.neutral[700], lineHeight: 1.45 }}>{r.summary || "—"}</TableCell>
                  <TableCell sx={{ ...cellSx, ...opsParamsColSx }} title={r.parameters?.[0] || undefined}>
                    {r.parameters?.length ? (
                      <Pill sx={{
                        fontSize: 12,
                        maxWidth: "100%",
                        minWidth: 0,
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}>
                        {r.parameters[0]}{r.parameters.length > 1 ? ` +${r.parameters.length - 1}` : ""}
                      </Pill>
                    ) : <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>—</Typography>}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
                      {(r.responses || []).slice(0, 4).map((c, j) => (
                        <Box key={j} sx={{
fontSize: 12, fontWeight: 600,
                          px: 0.9, py: 0.25, borderRadius: "5px",
                          ...statusCodeStyle(c),
                        }}>{c}</Box>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell sx={cellSx} align="right">
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ justifyContent: "flex-end" }}>
                      <Typography sx={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}>
                        {act ? nfmt(act.total_count) : "—"}
                      </Typography>
                      {act?.sparkline?.length
                        ? <Sparkline data={act.sparkline} color={invalid > 0 ? colors.yellow[100] : INDIGO} />
                        : null}
                      {invalid > 0 && <Pill tone="warn" sx={{ height: 18, px: 0.75 }}>{invalid} invalid</Pill>}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {filtered.length > 0 && (
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={OPS_PAGE_SIZE_OPTIONS}
            sx={{
              borderTop: `1px solid ${colors.neutral[200]}`,
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                fontSize: 14,
                color: colors.neutral[700],
              },
            }}
          />
        )}
      </TableContainer>
    </Box>
  );
}

const SCHEMA_MODE_OPTIONS = [
  { value: "off", label: "Off", icon: PowerOff, tone: "off" },
  { value: "log_only", label: "Log only", icon: Eye, tone: "log" },
  { value: "enforce", label: "Enforce", icon: ShieldCheck, tone: "enforce" },
];

const SPEC_POLICY_KEYS = ["mode", "closed_world", "body_validation_enabled", "response_validation_enabled"];

const SPEC_POLICY_DIFF_FIELDS = [
  { key: "mode", label: "Validation mode", format: (v) => SCHEMA_MODE_OPTIONS.find((m) => m.value === v)?.label ?? String(v ?? "—") },
  { key: "closed_world", label: "Closed world", format: enabledLabel },
  { key: "body_validation_enabled", label: "Body validation", format: enabledLabel },
  { key: "response_validation_enabled", label: "Response validation", format: enabledLabel },
];

function normalizeSpecPolicy(data) {
  const src = data || {};
  return {
    mode: src.mode || "off",
    closed_world: !!src.closed_world,
    body_validation_enabled: !!src.body_validation_enabled,
    response_validation_enabled: !!src.response_validation_enabled,
  };
}

function ToggleRow({ name, desc, checked, onChange, disabled, badge }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 1.75, borderBottom: `1px solid ${colors.neutral[200]}`, "&:last-child": { borderBottom: 0 }, opacity: disabled ? 0.6 : 1 }}
    >
      <Box sx={{ minWidth: 0, pr: 1.5 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
          {name}
          {badge ? <span style={{ fontWeight: 400, fontStyle: "italic", color: colors.neutral[600] }}> · {badge}</span> : null}
        </Typography>
        <Typography sx={{ fontSize: 12, color: colors.neutral[700], lineHeight: "17px", mt: 0.25 }}>
          {desc}
        </Typography>
      </Box>
      <GreenSwitch checked={checked} onChange={onChange} disabled={disabled} />
    </Stack>
  );
}

// ─── UPLOAD CARD ────────────────────────────────────────────────────
const fieldLabelSx = { fontSize: 12, color: colors.neutral[700], fontWeight: 500, mb: 0.75 };
const inputSx = {
  fontSize: 14,
  "& .MuiOutlinedInput-root": { borderRadius: "8px", height: 36, backgroundColor: colors.neutral[100] },
};

const SPEC_UPLOAD_MAX_BYTES = 10 * 1024 * 1024; // align with backend (>10 MB reject)
const SPEC_UPLOAD_MAX_LABEL = "10 MB";

function UploadCard({
  name, setName, version, setVersion, format, setFormat, fileRef, onSubmit,
  uploadErrors = {}, onClearUploadError, onFileSelected,
}) {
  const [fileName, setFileName] = React.useState("");
  return (
    <Box sx={{
      backgroundColor: colors.neutral[100], borderRadius: "12px",
      border: `1px solid ${colors.neutral[200]}`,
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ p: "16px 20px", borderBottom: `1px solid ${colors.neutral[200]}`, backgroundColor: FAINT_BLUE }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <UploadFileIcon sx={{ fontSize: 16, color: INDIGO }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Upload schema</Typography>
        </Stack>
        <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>
          OpenAPI 3 · GraphQL SDL · Protobuf
        </Typography>
      </Stack>

      <Box sx={{ p: 2.5 }}>
        <Box component="label" sx={{
          display: "flex", alignItems: "center", gap: 1.75,
          border: `1.5px dashed ${INDIGO_BORDER}`, borderRadius: "12px",
          backgroundColor: FAINT_BLUE, p: 2, cursor: "pointer",
          transition: "all 160ms",
          "&:hover": { borderColor: INDIGO, backgroundColor: "rgba(99,113,247,0.08)" },
        }}>
          <input
            ref={fileRef}
            type="file"
            accept=".yaml,.yml,.json,.proto,.graphql"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0] || null;
              if (f && f.size > SPEC_UPLOAD_MAX_BYTES) {
                e.target.value = "";
                setFileName("");
                onFileSelected?.(null, `File exceeds ${SPEC_UPLOAD_MAX_LABEL} limit`);
                return;
              }
              setFileName(f?.name || "");
              onFileSelected?.(f, null);
            }}
          />
          <Box sx={{
            width: 42, height: 42, borderRadius: "10px",
            backgroundColor: INDIGO_SOFT, color: INDIGO,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <UploadFileIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {fileName || "Drop a spec file or click to browse"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>
              YAML, JSON, .graphql or .proto · up to {SPEC_UPLOAD_MAX_LABEL}
            </Typography>
          </Box>
        </Box>
        {uploadErrors.file && (
          <Typography sx={{ fontSize: 12, color: "#d32f2f", mt: 0.75 }}>{uploadErrors.file}</Typography>
        )}

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25, mt: 1.75 }}>
          <AsecTextField
            fullWidth
            label="Name"
            placeholder="e.g. checkout_api"
            value={name}
            error={Boolean(uploadErrors.name)}
            helperText={uploadErrors.name || ""}
            onChange={(e) => {
              setName(e.target.value);
              onClearUploadError?.("name");
            }}
          />
          <AsecTextField fullWidth label="Version" placeholder="1.0.0"
            value={version} onChange={(e) => setVersion(e.target.value)} />
        </Box>

        <Box sx={{ mt: 1.25 }}>
          <Typography sx={fieldLabelSx}>Format</Typography>
          <Select size="small" fullWidth value={format}
            onChange={(e) => setFormat(e.target.value)} sx={inputSx}>
            <MenuItem value="openapi3">OpenAPI 3</MenuItem>
            <MenuItem value="graphql">GraphQL SDL</MenuItem>
            <MenuItem value="protobuf">Protobuf</MenuItem>
          </Select>
        </Box>

        <PrimaryButton
          startIcon={<UploadIcon />}
          onClick={onSubmit}
          sx={{ ...asecFormPrimaryButtonSx, mt: 1.75, width: "100%" }}
        >
          Upload &amp; review
        </PrimaryButton>
      </Box>
    </Box>
  );
}

// ─── RECENT INVALID FEED ────────────────────────────────────────────
function failureMessage(ev) {
  if (ev.result === "unmatched") return <>Path not declared in spec · <strong>closed-world</strong></>;
  if (ev.failure_kind && ev.failure_field) {
    return <><strong>{ev.failure_field}</strong> failed: {ev.failure_kind.replace(/_/g, " ")}</>;
  }
  if (ev.failure_kind) return <>{ev.failure_kind.replace(/_/g, " ")}</>;
  return "Validation failed";
}

function RecentInvalidFeed({ items, loading }) {
  return (
    <Box sx={{
      backgroundColor: colors.neutral[100], borderRadius: "12px",
      border: `1px solid ${colors.neutral[200]}`,
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ p: "16px 20px", borderBottom: `1px solid ${colors.neutral[200]}`, backgroundColor: FAINT_BLUE }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ReportGmailerrorredIcon sx={{ fontSize: 17, color: INDIGO }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Recent invalid requests</Typography>
        </Stack>
      </Stack>
      <Box sx={{ maxHeight: 520, overflow: "auto" }}>
        {loading ? (
          <Box sx={{ p: 3, textAlign: "center" }}><CircularProgress size={16} /></Box>
        ) : (items || []).length === 0 ? (
          <Typography sx={{ p: 3, textAlign: "center", color: colors.neutral[600], fontSize: 14 }}>
            No invalid requests in the last 24h.
          </Typography>
        ) : (items || []).map((ev, i) => (
          <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start" sx={{
            p: "12px 20px", borderBottom: `1px solid ${colors.neutral[200]}`,
            "&:last-child": { borderBottom: 0 },
            "&:hover": { backgroundColor: FAINT_GREY },
          }}>
            <Box sx={{
              width: 8, height: 8, borderRadius: "50%", flexShrink: 0, mt: 0.75,
              backgroundColor: ev.result === "unmatched" ? RED : YELLOW,
            }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
                <MethodTag method={ev.method} sx={{ minWidth: 50, height: 20 }} />
                <Typography sx={ { fontSize: 12, fontWeight: 500 }}>{ev.path}</Typography>
              </Stack>
              <Typography sx={{ fontSize: 12, color: colors.neutral[700], lineHeight: "25px", mt: 0.5 }}>
                {failureMessage(ev)}
              </Typography>
              <Typography sx={{ fontSize: 12, color: colors.neutral[600], mt: 0.5, fontVariantNumeric: "tabular-nums" }}>
                {relTime(ev.ts)} · {ev.src_ip || "—"}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Box>
    </Box>
  );
}

// ─── PAGE BODY ──────────────────────────────────────────────────────
// Spec-coverage "Unused operations" card — operations the active
// spec declares but the catalog hasn't seen traffic for in 7 days.
// Sourced from `api_security.spec_drift` rows of kind=unused
// (populated by the drift_detector cron). The "Mark resolved"
// action just sets `resolved_at` for the UI; if traffic shows up
// later the cron will reopen the row automatically.
function UnusedOperationsCard({ rows, onResolve }) {
  return (
    <Box sx={{
      borderRadius: "8px", border: `1px solid ${colors.neutral[200]}`,
      backgroundColor: "white", p: 3,
    }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <ReportGmailerrorredIcon sx={{ color: "#b45309", fontSize: 16 }} />
        <Typography sx={ { fontSize: 14, fontWeight: 600 }}>
          Unused operations <span style={{ color: colors.neutral[600], fontWeight: 400 }}>
            ({rows.length})
          </span>
        </Typography>
      </Stack>
      <Typography sx={ { fontSize: 12, color: colors.neutral[700], mb: 2 }}>
        Active spec declares these, but the catalog hasn't seen traffic for them in the last 7 days.
        Either they're internal, or they're stale — operator's call.
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: colors.neutral[100] }}>
              <TableCell sx={ { fontSize: 12, fontWeight: 600, color: colors.neutral[700] }}>Method</TableCell>
              <TableCell sx={ { fontSize: 12, fontWeight: 600, color: colors.neutral[700] }}>Path</TableCell>
              <TableCell sx={ { fontSize: 12, fontWeight: 600, color: colors.neutral[700] }}>Operation</TableCell>
              <TableCell sx={ { fontSize: 12, fontWeight: 600, color: colors.neutral[700] }} align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={ { fontSize: 12 }}>{r.method}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.path_template}</TableCell>
                <TableCell sx={ { fontSize: 12, color: colors.neutral[700] }}>
                  {r.detail?.operation_id || "—"}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => onResolve(r.id)}
                    sx={ { fontSize: 12, textTransform: "none" }}>
                    Mark resolved
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function Body({ siteId, fileRef: fileRefProp }) {
  const { showSuccess, showError } = useNotification();
  const { canConfigure, canWrite } = useAsecPermissions();
  const [specs, setSpecs] = React.useState(null);
  const [activeBaselineSpecId, setActiveBaselineSpecId] = React.useState(null);
  const [activeDraftSpecId, setActiveDraftSpecId] = React.useState(null);
  const [policyBaseline, setPolicyBaseline] = React.useState(null);
  const [policyDraft, setPolicyDraft] = React.useState(null);
  const [isApplyingPolicy, setIsApplyingPolicy] = React.useState(false);
  const [stats, setStats] = React.useState(null);
  const [statsLoading, setStatsLoading] = React.useState(true);
  const [selectedSpec, setSelectedSpec] = React.useState(null);
  const [parsedRows, setParsedRows] = React.useState(null);
  const [activity, setActivity] = React.useState({});
  const [recentInvalid, setRecentInvalid] = React.useState(null);
  // Synthesised candidate spec — the "draft built from observed
  // traffic" UX. When present we show a banner with accept/reject.
  // `candidateMeta` is the summary row; `candidateBusy` blocks
  // double-click during the accept/reject round-trip.
  const [candidateMeta, setCandidateMeta] = React.useState(null);
  const [candidateBusy, setCandidateBusy] = React.useState(null);
  // Open `unused` drift rows: spec declares operation, no traffic
  // in the last 7 days. The drift_detector cron is the writer.
  const [unusedDrift, setUnusedDrift] = React.useState([]);
  const [name, setName] = React.useState("");
  const [version, setVersion] = React.useState("");
  const [format, setFormat] = React.useState("openapi3");
  const [search, setSearch] = React.useState("");
  const [methodFilter, setMethodFilter] = React.useState("ALL");
  const [opsLive, setOpsLive] = React.useState(false);
  const [opsLoading, setOpsLoading] = React.useState(false);
  const [opsTick, setOpsTick] = React.useState(0);
  const fileRefLocal = React.useRef();
  const fileRef = fileRefProp || fileRefLocal;
  const [uploadErrors, setUploadErrors] = React.useState({});

  const reload = React.useCallback(async () => {
    if (!siteId) return;
    setStatsLoading(true);
    const [s, a, p, st, ra, cand, unused] = await Promise.all([
      asec.specList(siteId).catch(() => ({ data: [] })),
      asec.specActive(siteId).catch(() => ({ data: { active: null } })),
      asec.specPolicy(siteId).catch(() => ({ data: { mode: "off" } })),
      asec.specStats24h(siteId).catch(() => ({ data: null })),
      asec.specRecentInvalid(siteId, { limit: 20 }).catch(() => ({ data: { items: [] } })),
      asec.candidateList(siteId, { status: "pending", limit: 1 }).catch(() => ({ data: [] })),
      asec.driftList(siteId, { kind: "unused", open: "true", limit: 100 }).catch(() => ({ data: [] })),
    ]);
    setSpecs(Array.isArray(s.data) ? s.data : []);
    const activeSpecId = a.data?.spec_id ?? null;
    setActiveBaselineSpecId(activeSpecId);
    setActiveDraftSpecId(activeSpecId);
    const policy = normalizeSpecPolicy(p.data);
    setPolicyBaseline(policy);
    setPolicyDraft(policy);
    setStats(st.data);
    setStatsLoading(false);
    setRecentInvalid(ra.data?.items || []);
    setCandidateMeta(Array.isArray(cand.data) && cand.data[0] ? cand.data[0] : null);
    setUnusedDrift(Array.isArray(unused.data) ? unused.data : []);
    if (a.data?.spec_id) {
      const found = (s.data || []).find((x) => x.id === a.data.spec_id);
      if (found) {
        setSelectedSpec((prev) => (prev?.id === found.id ? prev : found));
      }
    }
  }, [siteId]);
  React.useEffect(() => { reload(); }, [reload]);

  const handleRefresh = React.useCallback(() => {
    reload();
    setOpsTick((n) => n + 1);
  }, [reload]);

  React.useEffect(() => {
    if (!siteId) return;
    asec.specOpsActivity(siteId, { limit: 200 })
      .then((r) => {
        const map = {};
        (r.data?.items || []).forEach((it) => {
          map[`${(it.method || "").toUpperCase()} ${it.path}`] = it;
        });
        setActivity(map);
      })
      .catch(() => setActivity({}));
  }, [siteId, opsTick]);

  React.useEffect(() => {
    if (!selectedSpec || !siteId) { setParsedRows(null); return; }
    let cancelled = false;
    setOpsLoading(true);
    (async () => {
      try {
        const r = await asec.specGet(siteId, selectedSpec.id);
        if (cancelled) return;
        const rows = rowsFromParsed(selectedSpec.format, r.data?.parsed);
        setParsedRows(rows);
      } catch (e) {
        if (!cancelled) setParsedRows([]);
      } finally {
        if (!cancelled) setOpsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSpec, siteId, opsTick]);

  React.useEffect(() => {
    if (!opsLive) return undefined;
    const h = setInterval(() => setOpsTick((n) => n + 1), 15000);
    return () => clearInterval(h);
  }, [opsLive]);

  // Candidate spec actions. Accept promotes the whole synthesised
  // draft into `api_security.specs` (a new row alongside any
  // existing ones) — does NOT activate. The user picks it from the
  // specs table below and clicks Activate when ready. Reject marks
  // the candidate so the next synthesis run supersedes it instead
  // of replacing in place.
  const acceptCandidate = async () => {
    if (!canWrite || !candidateMeta) return;
    setCandidateBusy("accept");
    try {
      const r = await asec.candidateAccept(siteId, candidateMeta.id);
      showSuccess(`Spec drafted: ${r.data?.spec_id?.slice(0, 8)}… — review in the Specs table below and click Activate to enforce.`);
      await reload();
    } catch (e) {
      showError(e?.response?.data?.message || "accept failed");
    } finally {
      setCandidateBusy(null);
    }
  };
  const rejectCandidate = async () => {
    if (!canConfigure || !candidateMeta) return;
    setCandidateBusy("reject");
    try {
      await asec.candidateReject(siteId, candidateMeta.id);
      showSuccess("Candidate rejected");
      await reload();
    } catch (e) {
      showError(e?.response?.data?.message || "reject failed");
    } finally {
      setCandidateBusy(null);
    }
  };
  const resolveDrift = async (id) => {
    if (!canConfigure) return;
    try {
      await asec.driftResolve(siteId, id);
      showSuccess("Drift marked resolved");
      await reload();
    } catch (e) {
      showError(e?.response?.data?.message || "resolve failed");
    }
  };

  const upload = async () => {
    if (!canWrite) return;
    const errors = {};
    if (!name.trim()) errors.name = "Name required";
    const f = fileRef.current?.files?.[0];
    if (!f) errors.file = "Pick a file";
    else if (f.size > SPEC_UPLOAD_MAX_BYTES) {
      errors.file = `File exceeds ${SPEC_UPLOAD_MAX_LABEL} limit`;
    }
    if (Object.keys(errors).length) {
      setUploadErrors(errors);
      return;
    }
    setUploadErrors({});
    const reader = new FileReader();
    reader.onerror = () => {
      setUploadErrors({ file: "Could not read file" });
      showError("Could not read file");
    };
    reader.onload = async (e) => {
      try {
        const dataUrl = e.target?.result;
        if (typeof dataUrl !== "string" || !dataUrl.includes(",")) {
          setUploadErrors({ file: "Could not read file" });
          showError("Could not read file");
          return;
        }
        await asec.specUpload(siteId, {
          name, version: version || undefined, format,
          raw: dataUrl.split(",")[1],
        });
        showSuccess("Spec uploaded");
        setName(""); setVersion("");
        if (fileRef.current) fileRef.current.value = "";
        reload();
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || String(err);
        setUploadErrors({ file: msg });
        showError(msg);
      }
    };
    reader.readAsDataURL(f);
  };

  const patchPolicy = React.useCallback((patch) => {
    if (!canConfigure) return;
    setPolicyDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }, [canConfigure]);

  const setActiveSpecDraft = React.useCallback((specId) => {
    if (!canConfigure) return;
    setActiveDraftSpecId(specId);
    const spec = (specs || []).find((s) => s.id === specId);
    if (spec) setSelectedSpec(spec);
  }, [canConfigure, specs]);

  const policyModifiedFields = React.useMemo(
    () => collectObjectDiff(policyBaseline, policyDraft, SPEC_POLICY_DIFF_FIELDS),
    [policyBaseline, policyDraft],
  );
  const policyModifiedCount = policyModifiedFields.length;

  const activeSpecModified = activeDraftSpecId !== activeBaselineSpecId;

  const modifiedFields = React.useMemo(() => {
    const fields = [...policyModifiedFields];
    if (activeSpecModified) {
      const fromSpec = (specs || []).find((s) => s.id === activeBaselineSpecId);
      const toSpec = (specs || []).find((s) => s.id === activeDraftSpecId);
      fields.push(fieldChange(
        "Active spec",
        fromSpec?.name || fromSpec?.title || "—",
        toSpec?.name || toSpec?.title || "—",
      ));
    }
    return fields;
  }, [
    activeBaselineSpecId,
    activeDraftSpecId,
    activeSpecModified,
    policyModifiedFields,
    specs,
  ]);

  const modifiedCount = modifiedFields.length;

  const discardPolicy = React.useCallback(() => {
    if (policyBaseline) setPolicyDraft({ ...policyBaseline });
    setActiveDraftSpecId(activeBaselineSpecId);
  }, [activeBaselineSpecId, policyBaseline]);

  const applyPolicy = React.useCallback(async () => {
    if (!siteId || !policyDraft || !canConfigure) return;
    setIsApplyingPolicy(true);
    try {
      if (policyModifiedCount > 0) {
        await asec.specPolicySet(siteId, policyDraft);
      }
      if (activeSpecModified && activeDraftSpecId) {
        await asec.specActivate(siteId, activeDraftSpecId);
      }
      showSuccess("Changes saved");
      await reload();
    } catch (e) {
      showError(e?.message || String(e));
    } finally {
      setIsApplyingPolicy(false);
    }
  }, [
    activeDraftSpecId,
    activeSpecModified,
    canConfigure,
    policyDraft,
    policyModifiedCount,
    reload,
    showError,
    showSuccess,
    siteId,
  ]);

  const specsWithCounts = React.useMemo(() => (specs || []).map((s) => {
    if (selectedSpec?.id === s.id && parsedRows) return { ...s, _op_count: parsedRows.length };
    return s;
  }), [specs, selectedSpec, parsedRows]);

  const activeSpec = activeDraftSpecId ? (specs || []).find((s) => s.id === activeDraftSpecId) : null;

  if (policyDraft === null) {
    return (
      <Box sx={{ py: 5, textAlign: "center", color: colors.neutral[700] }}>
        <CircularProgress size={24} sx={{ mb: 2 }} />
        <Typography>Loading validation policy…</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Synthesised candidate banner — appears whenever the
          api-catalog cron has a pending draft for this site. Accept
          creates a new `specs` row (NOT activated); reject marks it
          superseded. */}
      {candidateMeta && (
        <Box sx={{
          borderRadius: "8px", p: 2.5,
          border: `1px solid ${INDIGO_BORDER}`, backgroundColor: INDIGO_SOFT,
        }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} flexWrap="wrap">
            <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <LibraryBooksIcon sx={{ color: INDIGO_INK, fontSize: 16, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 14, lineHeight: "21px", color: INDIGO_INK, fontWeight: 600 }}>
                  Synthesised spec draft available
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[700], pl: 3.5 }}>
                Drafted from {Number(candidateMeta.sample_count || 0).toLocaleString()} observed samples ·{" "}
                {Number(candidateMeta.endpoints_count || 0)} operations ·{" "}
                {Number(candidateMeta.schemas_count || 0)} schemas
                · v{candidateMeta.version || "—"}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <GhostButton disabled={candidateBusy === "reject"} onClick={rejectCandidate}>Reject draft</GhostButton>
              <PrimaryButton disabled={candidateBusy === "accept"} onClick={acceptCandidate}>
                Accept as new spec
              </PrimaryButton>
            </Stack>
          </Stack>
          <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[600], mt: 1, pl: 3.5 }}>
            Accept creates a new spec but does NOT activate it. For surgical adds (one endpoint at a time), use
            "Add to spec" on undocumented rows in Discovery.
          </Typography>
        </Box>
      )}

      <AsecMetricStrip metrics={buildHeroMetrics(stats, statsLoading, policyDraft)} />

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={8}>
          <Stack spacing={2.5}>
            <SpecsCard
              specs={specsWithCounts}
              activeSpecId={activeDraftSpecId}
              onActiveChange={setActiveSpecDraft}
              onSelect={setSelectedSpec}
              canConfigure={canConfigure}
            />
            <OperationsExplorer
              selectedSpec={selectedSpec}
              rows={parsedRows}
              activityByKey={activity}
              search={search}
              setSearch={setSearch}
              methodFilter={methodFilter}
              setMethodFilter={setMethodFilter}
              loading={opsLoading}
              live={opsLive}
              setLive={setOpsLive}
              onRefresh={handleRefresh}
            />
            {activeSpec && unusedDrift.length > 0 && (
              <UnusedOperationsCard rows={unusedDrift} onResolve={resolveDrift} />
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={2.5}>
            {/* Validation settings */}
            <Box sx={{
              backgroundColor: colors.neutral[100], borderRadius: "12px",
              border: `1px solid ${colors.neutral[200]}`,
            }}>
              <Stack direction="row" alignItems="center"
                sx={{ p: "16px 20px", borderBottom: `1px solid ${colors.neutral[200]}`, backgroundColor: FAINT_BLUE }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Validation settings</Typography>
              </Stack>
              <Box sx={{ p: 2.5 }}>
                <Typography sx={{
fontSize: 12, fontWeight: 700, letterSpacing: "0.06em",
                  textTransform: "uppercase", color: colors.neutral[700], mb: 1,
                }}>
                  Enforcement mode
                </Typography>
                <AsecModeSegmented
                  value={policyDraft.mode}
                  onChange={(m) => patchPolicy({ mode: m })}
                  options={SCHEMA_MODE_OPTIONS}
                  disabled={!canConfigure}
                />

                {policyDraft?.mode === "log_only" && typeof stats?.would_block_24h === "number" && (
                  <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{
                    mt: 1.75, p: "12px 14px", borderRadius: "10px",
                    backgroundColor: INDIGO_SOFTER, border: `1px solid rgba(99,113,247,0.20)`,
                  }}>
                    <ShieldOutlinedIcon sx={{ fontSize: 16, color: INDIGO_INK, mt: 0.25 }} />
                    <Typography sx={{ fontSize: 12, color: INDIGO_INK, lineHeight: "17px" }}>
                      <strong>Observing without blocking.</strong>{" "}
                      {nfmt(stats.would_block_24h)} requests in the last 24h would have been
                      rejected under Enforce.
                    </Typography>
                  </Stack>
                )}

                <Box sx={{ mt: 0.5 }}>
                  <ToggleRow
                    name="Closed-world"
                    desc="Reject any path not declared in the spec (return 404)."
                    checked={policyDraft.closed_world}
                    onChange={() => patchPolicy({ closed_world: !policyDraft.closed_world })}
                    disabled={!canConfigure}
                  />
                  <ToggleRow
                    name="Body validation"
                    desc="Check request payloads against schemas."
                    checked={policyDraft.body_validation_enabled}
                    onChange={() => patchPolicy({ body_validation_enabled: !policyDraft.body_validation_enabled })}
                    disabled={!canConfigure}
                  />
                  <ToggleRow
                    name="Response validation"
                    desc="Flag responses whose body doesn't match the spec's response schema (observe-only)."
                    checked={policyDraft.response_validation_enabled}
                    onChange={() => patchPolicy({ response_validation_enabled: !policyDraft.response_validation_enabled })}
                    disabled={!canConfigure}
                  />
                </Box>
              </Box>
            </Box>

            <UploadCard
              name={name} setName={setName}
              version={version} setVersion={setVersion}
              format={format} setFormat={setFormat}
              fileRef={fileRef}
              onSubmit={upload}
              uploadErrors={uploadErrors}
              onClearUploadError={(key) => setUploadErrors((prev) => ({ ...prev, [key]: undefined }))}
              onFileSelected={(_file, errorMsg) => {
                setUploadErrors((prev) => ({
                  ...prev,
                  file: errorMsg || undefined,
                }));
              }}
            />

            <RecentInvalidFeed items={recentInvalid} loading={recentInvalid === null} />
          </Stack>
        </Grid>
      </Grid>

      <StickySaveBar
        open={modifiedCount > 0 && canConfigure}
        modifiedCount={modifiedCount}
        modifiedFields={modifiedFields}
        onDiscard={discardPolicy}
        onApply={applyPolicy}
        isApplying={isApplyingPolicy}
      />
    </Stack>
  );
}

// ─── Spec parsers (carried over from old impl) ──────────────────────
function rowsFromParsed(format, parsed) {
  if (!parsed) return [];
  if (format === "openapi3") return rowsFromOpenApi3(parsed);
  if (format === "graphql") return rowsFromGraphQL(parsed);
  return [];
}

function rowsFromOpenApi3(parsed) {
  const rows = [];
  const paths = parsed?.paths || {};
  for (const [path, ops] of Object.entries(paths)) {
    if (!ops || typeof ops !== "object") continue;
    for (const [method, op] of Object.entries(ops)) {
      if (!["get", "put", "post", "delete", "options", "head", "patch", "trace"].includes(method)) continue;
      const params = (op.parameters || []).map((p) => `${p.in}.${p.name}`);
      const responseCodes = Object.keys(op.responses || {});
      rows.push({
        method: method.toUpperCase(),
        path,
        summary: op.summary || op.operationId || "",
        parameters: params,
        responses: responseCodes,
        deprecated: !!op.deprecated,
      });
    }
  }
  return rows;
}

function rowsFromGraphQL(parsed) {
  const rows = [];
  const types = parsed?.types || parsed?.schema?.types || {};
  for (const [name, t] of Object.entries(types)) {
    if (!t?.fields) continue;
    for (const [fname, f] of Object.entries(t.fields)) {
      rows.push({
        method: t.kind || "FIELD",
        path: `${name}.${fname}`,
        summary: f.description || "",
        parameters: (f.args || []).map((a) => a.name),
        responses: [String(f.type || "")],
      });
    }
  }
  return rows;
}

export default function AsecSchemaValidation() {
  const fileRef = React.useRef();
  const [refreshKey, setRefreshKey] = React.useState(0);
  const { canWrite } = useAsecPermissions();

  return (
    <AsecPage featureKey="api.openapi_validation"
      helpTooltip="OpenAPI request validation. Pick a mode (Off / Log only / Enforce), upload a spec, activate it. Drift detection auto-flags endpoints observed in traffic but missing from the active spec."
      title="Schema Validation"
      helmetTitle="API Security — Schema Validation"
      pageDescription={(
        <>
          Reject requests that don&apos;t match your active OpenAPI, GraphQL or Protobuf spec.
          Start in <strong>Log only</strong> to see what would have been blocked before enforcing.
        </>
      )}
      onRefresh={() => setRefreshKey((k) => k + 1)}
      headerActions={(
        <>
          {canWrite ? (
          <PrimaryButton
            startIcon={<LayerdIcon icon={Upload} size="sm" color="inherit" />}
            onClick={() => fileRef.current?.click()}
          >
            Upload spec
          </PrimaryButton>
          ) : null}
        </>
      )}
    >
      {(siteId) => <Body key={refreshKey} siteId={siteId} fileRef={fileRef} />}
    </AsecPage>
  );
}
